import { datadogRum } from "@datadog/browser-rum"
import { Amplify } from "aws-amplify"
import { ArcElement, Chart as ChartJS, Tooltip } from "chart.js"
import { createRoot } from "react-dom/client"
import TagManager from "react-gtm-module"
import { HelmetProvider } from "react-helmet-async"
import { Provider } from "react-redux"
import { BrowserRouter } from "react-router-dom"
import { ToastContainer } from "react-toastify"
import "reflect-metadata"
import "unfetch/polyfill"

import { ReactQueryProvider } from "@front-shared/providers/reactQuery.provider.tsx"

import { AWS_CONFIG } from "./config/aws.config.ts"
import { RouterContainer } from "./containers/router.container.tsx"
import { ProduktlyHelper } from "./helpers/produktly.helper"
import { store } from "./store/store.ts"

import "@front-shared/assets/styles/antd.less"
import "@front-shared/assets/styles/overrides.less"

import "./assets/styles/index.less"
import "./assets/styles/tailwind.less"

ChartJS.register(ArcElement, Tooltip)

// INIT GOOGLE TAG MANAGER
if (import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID) {
    TagManager.initialize({
        gtmId: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID,
    })
}

// INIT DATADOG
if (
    import.meta.env.VITE_DD_APP_ID &&
    import.meta.env.VITE_DD_CLIENT_TOKEN &&
    import.meta.env.VITE_DD_SERVICE
) {
    datadogRum.init({
        applicationId: import.meta.env.VITE_DD_APP_ID,
        clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN,
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: "datadoghq.com",
        service: import.meta.env.VITE_DD_SERVICE,
        env: import.meta.env.VITE_ENV,
        version: import.meta.env.VITE_RELEASE_VERSION,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask",
    })
}

// Amplify
Amplify.configure(AWS_CONFIG.amplify)

// Produktly (product tours / announcements — token from env, not GTM)
ProduktlyHelper.init()

const root = document.getElementById("root")

if (root) {
    createRoot(root).render(
        // TODO: Enable StrictMode again when all issues are fixed - currently we need to refactor quite a few things
        // like Antd usage, etc.
        // because our elements are old, we get a lot of issues with strict mode remounting components twice, etc.
        // <StrictMode>
        <Provider store={store}>
            <HelmetProvider>
                <BrowserRouter>
                    <ReactQueryProvider>
                        <RouterContainer />
                    </ReactQueryProvider>
                </BrowserRouter>
            </HelmetProvider>

            <ToastContainer
                autoClose={4 * 1000}
                position="top-right"
                newestOnTop
                pauseOnHover
                closeOnClick
                hideProgressBar
            />
        </Provider>,
        // </StrictMode>,
    )
}

// Disable some errors in console in dev, due to React Strict Mode usage
if (import.meta.env.DEV) {
    // Ignore some warnings
    // issue to track: https://github.com/react-component/overflow/issues/23
    // for now convert all such errors to warnings in console, it's safe to do but less scary in console
    // doesn't seem to happen in prod build
    const ERRORS_TO_WARNINGS = [
        "Warning: Can't perform a React state update on an unmounted component",
        "Missing required elements",
    ]

    const consoleError = console.error

    // https://stackoverflow.com/questions/64158705/ignore-certain-console-errors-warnings-in-react
    console.error = function filterErrors(msg, ...args) {
        if (!ERRORS_TO_WARNINGS.some(entry => msg?.includes?.(entry))) {
            consoleError(msg, ...args)
        } else {
            console.warn(msg, ...args)
        }
    }
}
